import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  Shield,
  Zap,
  Globe,
  Lock,
  Server,
  RefreshCw,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronRight,
  Clock,
  Tag,
} from 'lucide-react';
import { fetchEvents, fetchEventStats } from '../services/applicationEventsService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LEVELS = ['INFO', 'WARN', 'ERROR'];
const CATEGORIES = ['SIGNING', 'VERIFICATION', 'SWITCH_COMM', 'AUTH', 'SYSTEM'];

const LEVEL_CONFIG = {
  INFO:  { label: 'Info',  color: 'bg-blue-100 text-blue-800',   icon: Info,          dot: 'bg-blue-400' },
  WARN:  { label: 'Warn',  color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, dot: 'bg-yellow-400' },
  ERROR: { label: 'Error', color: 'bg-red-100 text-red-800',     icon: AlertCircle,   dot: 'bg-red-500' },
};

const CAT_CONFIG = {
  SIGNING:      { label: 'Signing',      icon: Shield, color: 'bg-purple-100 text-purple-700' },
  VERIFICATION: { label: 'Verification', icon: Zap,    color: 'bg-indigo-100 text-indigo-700' },
  SWITCH_COMM:  { label: 'Switch Comm',  icon: Globe,  color: 'bg-teal-100 text-teal-700' },
  AUTH:         { label: 'Auth',         icon: Lock,   color: 'bg-orange-100 text-orange-700' },
  SYSTEM:       { label: 'System',       icon: Server, color: 'bg-gray-100 text-gray-700' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  return d.toLocaleString(undefined, {
    month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
}

function LevelBadge({ level }) {
  const cfg = LEVEL_CONFIG[level] ?? LEVEL_CONFIG.INFO;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function CategoryBadge({ category }) {
  const cfg = CAT_CONFIG[category] ?? { label: category, icon: Tag, color: 'bg-gray-100 text-gray-700' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function StatCard({ label, value, sub, accent }) {
  const accentMap = {
    red:    'border-l-red-500',
    yellow: 'border-l-yellow-400',
    blue:   'border-l-blue-500',
    gray:   'border-l-gray-400',
  };
  return (
    <div className={`bg-white rounded-lg border border-gray-200 border-l-4 ${accentMap[accent] ?? 'border-l-gray-400'} p-4`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value ?? '—'}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------

function DetailDrawer({ event, onClose }) {
  if (!event) return null;

  let parsedDetail = null;
  if (event.detail) {
    try { parsedDetail = JSON.parse(event.detail); } catch { /* raw string */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" aria-modal="true">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <aside className="relative z-10 w-full max-w-lg bg-white shadow-xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <LevelBadge level={event.level} />
            <CategoryBadge category={event.category} />
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Message */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Message</p>
            <p className="text-sm text-gray-900 font-medium">{event.message}</p>
          </div>

          {/* Meta grid */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-xs text-gray-400 uppercase tracking-wide">Time</dt>
              <dd className="text-gray-800 font-mono text-xs mt-0.5">{formatTime(event.eventTime)}</dd>
            </div>
            {event.correlationId && (
              <div>
                <dt className="text-xs text-gray-400 uppercase tracking-wide">Correlation ID</dt>
                <dd className="text-gray-800 font-mono text-xs mt-0.5 break-all">{event.correlationId}</dd>
              </div>
            )}
            {event.messageType && (
              <div>
                <dt className="text-xs text-gray-400 uppercase tracking-wide">Message Type</dt>
                <dd className="text-gray-800 font-mono text-xs mt-0.5">{event.messageType}</dd>
              </div>
            )}
            {event.direction && (
              <div>
                <dt className="text-xs text-gray-400 uppercase tracking-wide">Direction</dt>
                <dd className="text-gray-800 text-xs mt-0.5">{event.direction}</dd>
              </div>
            )}
            {event.actor && (
              <div className="col-span-2">
                <dt className="text-xs text-gray-400 uppercase tracking-wide">Actor (API Key)</dt>
                <dd className="text-gray-800 font-mono text-xs mt-0.5">{event.actor}</dd>
              </div>
            )}
          </dl>

          {/* Detail blob */}
          {event.detail && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Detail</p>
              {parsedDetail ? (
                <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(parsedDetail, null, 2)}
                </pre>
              ) : (
                <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-700 whitespace-pre-wrap break-words">
                  {event.detail}
                </pre>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ApplicationLogs() {
  // ── Filters ──────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    level: '', category: '', search: '', correlationId: '', from: '', to: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // ── Data ─────────────────────────────────────────────────────────────────
  const [events, setEvents]     = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // ── Detail drawer ─────────────────────────────────────────────────────────
  const [selected, setSelected] = useState(null);

  // ── Expanded rows (for inline detail on small screens) ────────────────────
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async (filterOverride) => {
    setLoading(true);
    setError(null);
    try {
      const active = filterOverride ?? appliedFilters;
      const [eventsData, statsData] = await Promise.all([
        fetchEvents({ limit: 200, ...active }),
        fetchEventStats(),
      ]);
      setEvents(eventsData);
      setStats(statsData);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => {
    const active = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== '')
    );
    setAppliedFilters(active);
    load(active);
  };

  const clearFilters = () => {
    setFilters({ level: '', category: '', search: '', correlationId: '', from: '', to: '' });
    setAppliedFilters({});
    load({});
  };

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Page title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Application Logs</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Structured event log — signing, verification, switch communication, and auth events.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-xs text-gray-400 hidden sm:inline">
              <Clock className="inline w-3 h-3 mr-1" />
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => load()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats banner */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard label="Errors (total)"     value={stats.totalErrors}    accent="red" />
          <StatCard label="Errors (24 h)"      value={stats.errorsLast24h}  accent="red"    sub="last 24 hours" />
          <StatCard label="Warnings (total)"   value={stats.totalWarns}     accent="yellow" />
          <StatCard label="Auth rejections"    value={stats.authRejections} accent="blue" />
          <StatCard label="Switch failures"    value={stats.switchFailures} accent="gray" />
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        {/* Search + toggle */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search message or correlation ID…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${
              showFilters
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary-600 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 border-t border-gray-100">
            {/* Level */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Level</label>
              <select
                value={filters.level}
                onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All</option>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_CONFIG[c]?.label ?? c}</option>)}
              </select>
            </div>

            {/* From */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input
                type="datetime-local"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* To */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input
                type="datetime-local"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <X className="w-3 h-3" /> Clear filters
            </button>
          )}
          <button
            onClick={applyFilters}
            className="px-4 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Failed to load events</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Table header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {loading ? 'Loading…' : `${events.length} event${events.length !== 1 ? 's' : ''}`}
          </span>
          <span className="text-xs text-gray-400">Showing most recent 200 • click row for detail</span>
        </div>

        {loading && events.length === 0 ? (
          <div className="py-16 flex flex-col items-center text-gray-400 gap-2">
            <RefreshCw className="w-8 h-8 animate-spin" />
            <p className="text-sm">Loading events…</p>
          </div>
        ) : events.length === 0 ? (
          <div className="py-16 flex flex-col items-center text-gray-400 gap-2">
            <Info className="w-8 h-8" />
            <p className="text-sm">No events found matching the current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-4 py-2" />
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">Time</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Level</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Category</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Message</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Correlation ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Msg Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Actor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {events.map((ev) => {
                  const isExpanded = expandedRows.has(ev.id);
                  const rowBg = ev.level === 'ERROR'
                    ? 'bg-red-50 hover:bg-red-100'
                    : ev.level === 'WARN'
                    ? 'bg-yellow-50 hover:bg-yellow-100'
                    : 'hover:bg-gray-50';

                  return (
                    <React.Fragment key={ev.id}>
                      <tr
                        className={`cursor-pointer transition-colors ${rowBg}`}
                        onClick={() => setSelected(ev)}
                      >
                        {/* Expand toggle */}
                        <td className="px-4 py-2" onClick={(e) => { e.stopPropagation(); toggleRow(ev.id); }}>
                          {ev.detail ? (
                            isExpanded
                              ? <ChevronDown className="w-4 h-4 text-gray-400" />
                              : <ChevronRight className="w-4 h-4 text-gray-400" />
                          ) : null}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-gray-500 whitespace-nowrap">
                          {formatTime(ev.eventTime)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <LevelBadge level={ev.level} />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <CategoryBadge category={ev.category} />
                        </td>
                        <td className="px-4 py-2 text-gray-800 max-w-xs truncate" title={ev.message}>
                          {ev.message}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500 max-w-xs truncate">
                          {ev.correlationId ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {ev.messageType ?? '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">
                          {ev.actor ?? '—'}
                        </td>
                      </tr>

                      {/* Inline expanded detail row */}
                      {isExpanded && ev.detail && (
                        <tr className="bg-gray-50">
                          <td colSpan={8} className="px-8 py-3">
                            <pre className="text-xs text-gray-600 whitespace-pre-wrap break-words bg-white border border-gray-200 rounded p-3 max-h-60 overflow-y-auto">
                              {(() => {
                                try { return JSON.stringify(JSON.parse(ev.detail), null, 2); }
                                catch { return ev.detail; }
                              })()}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer (click-through) */}
      {selected && (
        <DetailDrawer event={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
